import { clubSlugFromLocation } from './club-slug';
import { awaitCrest } from './crest';
import { WHITE_LABEL } from './whitelabel';

/**
 * The browser's own chrome for this page - the tab's icon and its title.
 *
 * They ship in `index.html` as HUDDLE's, which is right for the shared app
 * and wrong for a club: on `dublinraiders.huddlesports.ie` the goalposts sit
 * in the tab, and the word "Huddle" beside them, from the first byte until
 * the club's brand resolves a moment later. Small, and exactly the kind of
 * seam that tells a member whose software this really is.
 *
 * So on a club's address the chrome is HELD at startup - blanked, not
 * replaced, because the club's name is not known yet - and filled in when
 * the brand lands. A baked flavour and the shared app both skip all of it:
 * their chrome was already right.
 */

/** Huddle's own, remembered the first time this module touches the tab -
 *  LAZILY, so it never depends on when the module happens to be imported
 *  relative to the document it is describing. Whoever moves first (the hold
 *  at startup, or a brand landing) snapshots before changing anything. */
let huddleIcon: string | null = null;
let huddleTitle: string | null = null;

function iconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel="icon"]');
}

function remember(): void {
  huddleIcon ??= iconLink()?.getAttribute('href') || null;
  huddleTitle ??= document.title || null;
}

/**
 * Blank the tab's icon and title when this page belongs to a club whose
 * brand is not known yet. Called once, before bootstrap.
 *
 * An empty title leaves the address showing, which says nothing about
 * anybody - the point is only that it does not say Huddle.
 */
export function holdClubChrome(): void {
  if (WHITE_LABEL || !clubSlugFromLocation()) return;
  remember();
  iconLink()?.removeAttribute('href');
  document.title = '';
}

/**
 * The club has landed: its name in the tab straight away, its crest as soon
 * as the crest is actually a picture.
 *
 * The icon WAITS. Pointing the link at a URL only starts a fetch - the tab
 * sits on the browser's own placeholder until it lands, and keeps a broken
 * one forever if it never does. Waiting for the decode (`awaitCrest`, which
 * the boot veil is already waiting on, so this costs nothing extra) means
 * the tab goes from bare to the club's crest in one step, or stays bare.
 *
 * Bare is the right failure: on a club's page the alternative is HUDDLE's
 * goalposts, and another company's mark is worse than no mark at all.
 */
export function applyClubChrome(config: { logoUrl: string | null; appName: string }): void {
  remember();
  document.title = config.appName;
  void showCrest(config.logoUrl);
}

async function showCrest(url: string | null): Promise<void> {
  if (!url || !(await awaitCrest(url))) return;
  const link = iconLink();
  if (!link) return;
  link.href = url;
  // A crest is a PNG/JPEG from storage; the shipped mark is an SVG data URI,
  // and a stale type stops some browsers drawing the new one.
  link.type = url.startsWith('data:image/svg') ? 'image/svg+xml' : '';
}

/** Back to Huddle's own - the shared app, or a club we could not resolve. */
export function restoreHuddleChrome(): void {
  remember();
  if (huddleTitle) document.title = huddleTitle;
  const link = iconLink();
  if (!link || !huddleIcon) return;
  link.href = huddleIcon;
  link.type = 'image/svg+xml';
}
